import { Link } from 'react-router-dom'
import { ArrowRight, MessageSquare, Zap, Shield, Users } from 'lucide-react'
import Header from '../components/Header'

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500/10 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/50"></div>
          <div className="relative max-w-7xl mx-auto text-center">
                <div className="animate-fade-in">
                  <div className="mb-8">
                    <img
                      src="/assets/ifdlogo.png"
                      alt="Inland Fiber AI Logo"
                      className="h-20 w-20 mx-auto mb-4"
                    />
                  </div>
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" style={{color: '#f9fafb'}}>
                Chat with the
                <span className="gradient-text block">Future of AI</span>
              </h1>
              <p className="text-xl md:text-2xl mb-10 max-w-4xl mx-auto leading-relaxed" style={{color: '#d1d5db'}}>
                Experience unlimited AI conversations with the most advanced models. 
                Upload files, ask complex questions, and get intelligent responses from GPT, Claude, and more.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link to="/register" className="btn btn-primary group" style={{fontSize: '1.1rem', padding: '1rem 2.5rem', minWidth: '200px'}}>
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/pricing" className="btn btn-outline" style={{fontSize: '1.1rem', padding: '1rem 2.5rem', minWidth: '200px'}}>
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{color: '#f9fafb'}}>
                Why Choose Inland Fiber AI?
              </h2>
              <p className="text-xl max-w-2xl mx-auto" style={{color: '#d1d5db'}}>
                Powerful AI chat features designed to enhance your productivity and creativity
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center card p-8 card-hover animate-slide-up">
                <div className="feature-icon">
                  <MessageSquare className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{color: '#f9fafb'}}>
                  Unlimited AI Conversations
                </h3>
                <p className="leading-relaxed" style={{color: '#d1d5db'}}>
                  Chat with multiple AI models including GPT-4, Claude, and more. No limits on conversations or questions.
                </p>
              </div>

              <div className="text-center card p-8 card-hover animate-slide-up" style={{animationDelay: '0.1s'}}>
                <div className="feature-icon">
                  <Zap className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{color: '#f9fafb'}}>
                  Lightning Fast Responses
                </h3>
                <p className="leading-relaxed" style={{color: '#d1d5db'}}>
                  Get instant, intelligent responses from the most advanced AI models. Optimized for speed and accuracy.
                </p>
              </div>

              <div className="text-center card p-8 card-hover animate-slide-up" style={{animationDelay: '0.2s'}}>
                <div className="feature-icon">
                  <Shield className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{color: '#f9fafb'}}>
                  File Upload & Analysis
                </h3>
                <p className="leading-relaxed" style={{color: '#d1d5db'}}>
                  Upload documents, images, and files for AI analysis. Get insights, summaries, and intelligent responses.
                </p>
              </div>

              <div className="text-center card p-8 card-hover animate-slide-up" style={{animationDelay: '0.3s'}}>
                <div className="feature-icon">
                  <Users className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{color: '#f9fafb'}}>
                  Multiple AI Models
                </h3>
                <p className="leading-relaxed" style={{color: '#d1d5db'}}>
                  Access GPT-4, Claude, Gemini, and other cutting-edge AI models. Choose the best model for your needs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20" style={{background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'}}>
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{color: '#f9fafb'}}>
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8" style={{color: '#d1d5db'}}>
              Join thousands of users who are already chatting with AI to boost their productivity and creativity.
            </p>
            <Link to="/register" className="btn btn-primary" style={{fontSize: '1.1rem', padding: '1rem 2.5rem', minWidth: '220px'}}>
              Start Your Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)', color: 'white'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <img
                    src="/assets/EPLogoTsp.png"
                    alt="Inland Fiber AI Logo"
                    className="h-8 w-8 mr-3"
                  />
                  <h3 className="text-2xl font-bold" style={{color: '#f8fafc'}}>Inland Fiber AI</h3>
                </div>
            <p className="mb-4" style={{color: '#cbd5e1'}}>
              The future of AI conversation is here
            </p>
            <div className="flex justify-center space-x-6">
              <Link to="/pricing" className="hover:text-white transition-colors" style={{color: '#cbd5e1'}}>
                Pricing
              </Link>
              <Link to="/login" className="hover:text-white transition-colors" style={{color: '#cbd5e1'}}>
                Login
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
